import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import DescriptionIcon from "@mui/icons-material/Description";
import DownloadIcon from "@mui/icons-material/Download";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import {
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    Grid,
    IconButton,
    Paper,
    Typography,
} from "@mui/material";
import { useState } from "react";
import { toast } from "react-toastify";

const ALLOWED_DOC_EXTENSIONS = new Set([
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "bmp",
  "pdf",
]);
const MAX_DOC_BYTES = 10 * 1024 * 1024;

const getFileExtension = (fileName = "") => {
  const parts = String(fileName).split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
};

const validateRegistrationFiles = (files) => {
  const accepted = [];
  const rejected = [];
  for (const file of files) {
    const ext = getFileExtension(file?.name);
    if (!ext || !ALLOWED_DOC_EXTENSIONS.has(ext)) {
      rejected.push(
        `${file?.name || "فایل"}: فقط JPG، PNG، WEBP، GIF، BMP یا PDF مجاز است`
      );
      continue;
    }
    if (!file?.size || file.size > MAX_DOC_BYTES) {
      rejected.push(`${file?.name || "فایل"}: حداکثر حجم هر فایل ۱۰ مگابایت است`);
      continue;
    }
    accepted.push(file);
  }
  return { accepted, rejected };
};

const DocumentsStep = ({ formData, setFormData, errors, setErrors }) => {
  const [uploadProgress, setUploadProgress] = useState({});

  const handleFileUpload =
    (field, multiple = false) =>
    (event) => {
      const selected = Array.from(event.target.files || []);
      event.target.value = "";
      if (selected.length === 0) return;

      const { accepted, rejected } = validateRegistrationFiles(selected);
      if (rejected.length > 0) {
        const message = rejected.slice(0, 3).join(" — ");
        setErrors((prev) => ({
          ...prev,
          [field]: message,
        }));
        toast.error(message);
      }
      if (accepted.length === 0) return;

      if (multiple) {
        setFormData((prev) => ({
          ...prev,
          [field]: [...(prev[field] || []), ...accepted],
        }));
      } else {
        const uploadedFile = accepted[0];
        console.log("آپلود فایل:", field, uploadedFile);
        setFormData((prev) => {
          const newData = {
            ...prev,
            [field]: uploadedFile,
          };
          console.log("formData جدید:", newData);
          return newData;
        });
      }

      // Clear error when valid file is uploaded
      if (accepted.length > 0) {
        setErrors((prev) => ({
          ...prev,
          [field]: "",
        }));
      }

      // Simulate upload progress
      accepted.forEach((file, index) => {
        const fileId = `${field}_${index}_${Date.now()}`;
        setUploadProgress((prev) => ({ ...prev, [fileId]: 0 }));

        const interval = setInterval(() => {
          setUploadProgress((prev) => {
            const newProgress = (prev[fileId] || 0) + 10;
            if (newProgress >= 100) {
              clearInterval(interval);
              const { [fileId]: removed, ...rest } = prev;
              return rest;
            }
            return { ...prev, [fileId]: newProgress };
          });
        }, 100);
      });
    };

  const handleFileRemove =
    (field, fileIndex = null) =>
    () => {
      if (fileIndex !== null) {
        setFormData((prev) => ({
          ...prev,
          [field]: prev[field].filter((_, index) => index !== fileIndex),
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          [field]: null,
        }));
      }
    };

  const getFileIcon = (fileName) => {
    const extension = fileName.split(".").pop().toLowerCase();
    switch (extension) {
      case "pdf":
        return <PictureAsPdfIcon color="error" />;
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
        return <ImageIcon color="primary" />;
      default:
        return <DescriptionIcon color="secondary" />;
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 600 }}>
        بارگذاری مدارک
      </Typography>

      {formData.registrationType === "individual" && (
        <Box
          sx={{
            mb: 3,
            p: 2,
            bgcolor: "info.lighter",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "info.light",
          }}
        >
          <Typography variant="body2" color="info.main">
            برای ثبت‌نام حقیقی (شخصی)، فقط بارگذاری کارت ملی و نمونه کارها الزامی است.
          </Typography>
        </Box>
      )}

      <Grid container spacing={3}>
        {/* National Card */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              height: "100%",
              border: errors.nationalCardImage ? "2px solid" : "none",
              borderColor: "error.main",
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom>
                تصویر کارت ملی *
              </Typography>
              {formData.nationalCardImage ? (
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    {getFileIcon(formData.nationalCardImage.name)}
                    <Typography variant="body2" sx={{ ml: 1, flex: 1 }}>
                      {formData.nationalCardImage.name}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={handleFileRemove("nationalCardImage")}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                  <Chip label="آپلود شده" color="success" size="small" />
                </Box>
              ) : (
                <Box sx={{ textAlign: "center", py: 3 }}>
                  <CloudUploadIcon
                    sx={{
                      fontSize: 48,
                      color: errors.nationalCardImage
                        ? "error.main"
                        : "text.secondary",
                      mb: 2,
                    }}
                  />
                  <Typography
                    variant="body2"
                    color={
                      errors.nationalCardImage ? "error" : "text.secondary"
                    }
                    gutterBottom
                  >
                    {formData.registrationType === "legal"
                      ? "کارت ملی مدیرعامل یا نماینده شرکت"
                      : "تصویر واضح از روی کارت ملی"
                    }
                  </Typography>
                  {errors.nationalCardImage && (
                    <Chip
                      label="آپلود نشده"
                      color="error"
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  )}
                </Box>
              )}
              {errors.nationalCardImage && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 1, display: "block", fontWeight: "bold" }}
                >
                  {errors.nationalCardImage}
                </Typography>
              )}
            </CardContent>
            <CardActions>
              <Button
                component="label"
                variant={errors.nationalCardImage ? "contained" : "outlined"}
                color={errors.nationalCardImage ? "error" : "primary"}
                startIcon={<CloudUploadIcon />}
                fullWidth
              >
                {formData.nationalCardImage ? "تغییر فایل" : "انتخاب فایل"}
                <input
                  type="file"
                  hidden
                  accept=".jpg,.jpeg,.png,.gif,.webp,.bmp,.pdf,image/jpeg,image/png,image/gif,image/webp,image/bmp,application/pdf"
                  onChange={handleFileUpload("nationalCardImage")}
                />
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Profile Image (for Individual) or Company Logo (for Legal) */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              height: "100%",
              border:
                (formData.registrationType === "individual" &&
                  errors.profileImage) ||
                (formData.registrationType === "legal" && errors.companyLogo)
                  ? "2px solid"
                  : "none",
              borderColor: "error.main",
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {formData.registrationType === "individual"
                  ? "عکس پرسنلی *"
                  : "لوگوی شرکت *"}
              </Typography>
              {(formData.registrationType === "individual"
                ? formData.profileImage
                : formData.companyLogo) ? (
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    {getFileIcon(
                      formData.registrationType === "individual"
                        ? formData.profileImage.name
                        : formData.companyLogo.name
                    )}
                    <Typography variant="body2" sx={{ ml: 1, flex: 1 }}>
                      {formData.registrationType === "individual"
                        ? formData.profileImage.name
                        : formData.companyLogo.name}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={handleFileRemove(
                        formData.registrationType === "individual"
                          ? "profileImage"
                          : "companyLogo"
                      )}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                  <Chip label="آپلود شده" color="success" size="small" />
                </Box>
              ) : (
                <Box sx={{ textAlign: "center", py: 3 }}>
                  <CloudUploadIcon
                    sx={{
                      fontSize: 48,
                      color:
                        (formData.registrationType === "individual" &&
                          errors.profileImage) ||
                        (formData.registrationType === "legal" &&
                          errors.companyLogo)
                          ? "error.main"
                          : "text.secondary",
                      mb: 2,
                    }}
                  />
                  <Typography
                    variant="body2"
                    color={
                      (formData.registrationType === "individual" &&
                        errors.profileImage) ||
                      (formData.registrationType === "legal" &&
                        errors.companyLogo)
                        ? "error"
                        : "text.secondary"
                    }
                    gutterBottom
                  >
                    {formData.registrationType === "individual"
                      ? "تصویر پرسنلی با کیفیت خوب"
                      : "لوگو با فرمت PNG یا JPG"}
                  </Typography>
                  {((formData.registrationType === "individual" &&
                    errors.profileImage) ||
                    (formData.registrationType === "legal" &&
                      errors.companyLogo)) && (
                    <Chip
                      label="آپلود نشده"
                      color="error"
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  )}
                </Box>
              )}
              {formData.registrationType === "individual" &&
                errors.profileImage && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 1, display: "block", fontWeight: "bold" }}
                  >
                    {errors.profileImage}
                  </Typography>
                )}
              {formData.registrationType === "legal" && errors.companyLogo && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 1, display: "block", fontWeight: "bold" }}
                >
                  {errors.companyLogo}
                </Typography>
              )}
            </CardContent>
            <CardActions>
              <Button
                component="label"
                variant={
                  (formData.registrationType === "individual" &&
                    errors.profileImage) ||
                  (formData.registrationType === "legal" && errors.companyLogo)
                    ? "contained"
                    : "outlined"
                }
                color={
                  (formData.registrationType === "individual" &&
                    errors.profileImage) ||
                  (formData.registrationType === "legal" && errors.companyLogo)
                    ? "error"
                    : "primary"
                }
                startIcon={<CloudUploadIcon />}
                fullWidth
              >
                {(formData.registrationType === "individual"
                  ? formData.profileImage
                  : formData.companyLogo)
                  ? "تغییر فایل"
                  : "انتخاب فایل"}
                <input
                  type="file"
                  hidden
                  accept=".jpg,.jpeg,.png,.gif,.webp,.bmp,image/jpeg,image/png,image/gif,image/webp,image/bmp"
                  onChange={handleFileUpload(
                    formData.registrationType === "individual"
                      ? "profileImage"
                      : "companyLogo"
                  )}
                />
              </Button>
            </CardActions>
          </Card>
        </Grid>

        {/* Company License - فقط برای حقوقی */}
        {formData.registrationType === "legal" && (
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: "100%",
                border: errors.companyLicenseImage ? "2px solid" : "none",
                borderColor: "error.main",
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  مجوز شرکت *
                </Typography>
              {formData.companyLicenseImage ? (
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    {getFileIcon(formData.companyLicenseImage.name)}
                    <Typography variant="body2" sx={{ ml: 1, flex: 1 }}>
                      {formData.companyLicenseImage.name}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={handleFileRemove("companyLicenseImage")}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                  <Chip label="آپلود شده" color="success" size="small" />
                </Box>
              ) : (
                <Box sx={{ textAlign: "center", py: 3 }}>
                  <CloudUploadIcon
                    sx={{
                      fontSize: 48,
                      color: errors.companyLicenseImage
                        ? "error.main"
                        : "text.secondary",
                      mb: 2,
                    }}
                  />
                  <Typography
                    variant="body2"
                    color={
                      errors.companyLicenseImage ? "error" : "text.secondary"
                    }
                    gutterBottom
                  >
                    مجوز فعالیت، جواز تأسیس یا کارت بازرگانی
                  </Typography>
                  {errors.companyLicenseImage && (
                    <Chip
                      label="آپلود نشده"
                      color="error"
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  )}
                </Box>
              )}
              {errors.companyLicenseImage && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 1, display: "block", fontWeight: "bold" }}
                >
                  {errors.companyLicenseImage}
                </Typography>
              )}
            </CardContent>
            <CardActions>
              <Button
                component="label"
                variant={errors.companyLicenseImage ? "contained" : "outlined"}
                color={errors.companyLicenseImage ? "error" : "primary"}
                startIcon={<CloudUploadIcon />}
                fullWidth
              >
                {formData.companyLicenseImage ? "تغییر فایل" : "انتخاب فایل"}
                <input
                  type="file"
                  hidden
                  accept=".jpg,.jpeg,.png,.gif,.webp,.bmp,.pdf,image/jpeg,image/png,image/gif,image/webp,image/bmp,application/pdf"
                  onChange={handleFileUpload("companyLicenseImage")}
                />
              </Button>
            </CardActions>
          </Card>
        </Grid>
        )}

        {/* Official Registration Documents - فقط برای حقوقی */}
        {formData.registrationType === "legal" && (
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: "100%",
                border: errors.officialRegistrationDocumentsImage
                  ? "2px solid"
                  : "none",
                borderColor: "error.main",
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  مدارک ثبتی شرکت *
                </Typography>
                {formData.officialRegistrationDocumentsImage ? (
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      {getFileIcon(formData.officialRegistrationDocumentsImage.name)}
                      <Typography variant="body2" sx={{ ml: 1, flex: 1 }}>
                        {formData.officialRegistrationDocumentsImage.name}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={handleFileRemove("officialRegistrationDocumentsImage")}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    <Chip label="آپلود شده" color="success" size="small" />
                  </Box>
                ) : (
                  <Box sx={{ textAlign: "center", py: 3 }}>
                    <CloudUploadIcon
                      sx={{
                        fontSize: 48,
                        color: errors.officialRegistrationDocumentsImage
                          ? "error.main"
                          : "text.secondary",
                        mb: 2,
                      }}
                    />
                    <Typography
                      variant="body2"
                      color={
                        errors.officialRegistrationDocumentsImage
                          ? "error"
                          : "text.secondary"
                      }
                      gutterBottom
                    >
                      روزنامه رسمی، آگهی تاسیس یا آخرین تغییرات شرکت
                    </Typography>
                  </Box>
                )}
                {errors.officialRegistrationDocumentsImage && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 1, display: "block", fontWeight: "bold" }}
                  >
                    {errors.officialRegistrationDocumentsImage}
                  </Typography>
                )}
              </CardContent>
              <CardActions>
                <Button
                  component="label"
                  variant={
                    errors.officialRegistrationDocumentsImage
                      ? "contained"
                      : "outlined"
                  }
                  color={
                    errors.officialRegistrationDocumentsImage ? "error" : "primary"
                  }
                  startIcon={<CloudUploadIcon />}
                  fullWidth
                >
                  {formData.officialRegistrationDocumentsImage
                    ? "تغییر فایل"
                    : "انتخاب فایل"}
                  <input
                    type="file"
                    hidden
                    accept=".jpg,.jpeg,.png,.gif,.webp,.bmp,.pdf,image/jpeg,image/png,image/gif,image/webp,image/bmp,application/pdf"
                    onChange={handleFileUpload("officialRegistrationDocumentsImage")}
                  />
                </Button>
              </CardActions>
            </Card>
          </Grid>
        )}

        {/* Authorized Signatories - فقط برای حقوقی */}
        {formData.registrationType === "legal" && (
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: "100%",
                border: errors.authorizedSignatoriesImage ? "2px solid" : "none",
                borderColor: "error.main",
              }}
            >
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  مدارک صاحبان امضا *
                </Typography>
                {formData.authorizedSignatoriesImage ? (
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      {getFileIcon(formData.authorizedSignatoriesImage.name)}
                      <Typography variant="body2" sx={{ ml: 1, flex: 1 }}>
                        {formData.authorizedSignatoriesImage.name}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={handleFileRemove("authorizedSignatoriesImage")}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                    <Chip label="آپلود شده" color="success" size="small" />
                  </Box>
                ) : (
                  <Box sx={{ textAlign: "center", py: 3 }}>
                    <CloudUploadIcon
                      sx={{
                        fontSize: 48,
                        color: errors.authorizedSignatoriesImage
                          ? "error.main"
                          : "text.secondary",
                        mb: 2,
                      }}
                    />
                    <Typography
                      variant="body2"
                      color={
                        errors.authorizedSignatoriesImage ? "error" : "text.secondary"
                      }
                      gutterBottom
                    >
                      معرفی/نمونه امضا صاحبان امضای مجاز شرکت
                    </Typography>
                  </Box>
                )}
                {errors.authorizedSignatoriesImage && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 1, display: "block", fontWeight: "bold" }}
                  >
                    {errors.authorizedSignatoriesImage}
                  </Typography>
                )}
              </CardContent>
              <CardActions>
                <Button
                  component="label"
                  variant={errors.authorizedSignatoriesImage ? "contained" : "outlined"}
                  color={errors.authorizedSignatoriesImage ? "error" : "primary"}
                  startIcon={<CloudUploadIcon />}
                  fullWidth
                >
                  {formData.authorizedSignatoriesImage ? "تغییر فایل" : "انتخاب فایل"}
                  <input
                    type="file"
                    hidden
                    accept=".jpg,.jpeg,.png,.gif,.webp,.bmp,.pdf,image/jpeg,image/png,image/gif,image/webp,image/bmp,application/pdf"
                    onChange={handleFileUpload("authorizedSignatoriesImage")}
                  />
                </Button>
              </CardActions>
            </Card>
          </Grid>
        )}

        {/* Work Samples */}
        <Grid item xs={12}>
          <Card
            sx={{
              border: errors.workSamplesImages ? "2px solid" : "none",
              borderColor: "error.main",
            }}
          >
            <CardContent>
              <Typography variant="h6" gutterBottom>
                نمونه کارها *
              </Typography>
              {formData.workSamplesImages &&
              formData.workSamplesImages.length > 0 ? (
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {formData.workSamplesImages.map((file, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Paper sx={{ p: 2 }}>
                        <Box
                          sx={{ display: "flex", alignItems: "center", mb: 1 }}
                        >
                          {getFileIcon(file.name)}
                          <Typography
                            variant="body2"
                            sx={{ ml: 1, flex: 1 }}
                            noWrap
                          >
                            {file.name}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={handleFileRemove(
                              "workSamplesImages",
                              index
                            )}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                        <Chip label="آپلود شده" color="success" size="small" />
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ textAlign: "center", py: 3 }}>
                  <CloudUploadIcon
                    sx={{
                      fontSize: 48,
                      color: errors.workSamplesImages
                        ? "error.main"
                        : "text.secondary",
                      mb: 2,
                    }}
                  />
                  <Typography
                    variant="body2"
                    color={
                      errors.workSamplesImages ? "error" : "text.secondary"
                    }
                    gutterBottom
                  >
                    تصاویر از پروژه‌های قبلی شما
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    JPG، PNG، WEBP یا PDF — حداکثر ۱۰ مگابایت برای هر فایل
                  </Typography>
                  {errors.workSamplesImages && (
                    <Chip
                      label="آپلود نشده"
                      color="error"
                      size="small"
                      sx={{ mt: 1 }}
                    />
                  )}
                </Box>
              )}
              {errors.workSamplesImages && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 1, display: "block", fontWeight: "bold" }}
                >
                  {errors.workSamplesImages}
                </Typography>
              )}
            </CardContent>
            <CardActions>
              <Button
                component="label"
                variant={errors.workSamplesImages ? "contained" : "outlined"}
                color={errors.workSamplesImages ? "error" : "primary"}
                startIcon={<CloudUploadIcon />}
                fullWidth
              >
                {formData.workSamplesImages &&
                formData.workSamplesImages.length > 0
                  ? "افزودن نمونه کار بیشتر"
                  : "انتخاب نمونه کار"}
                <input
                  type="file"
                  hidden
                  multiple
                  accept=".jpg,.jpeg,.png,.gif,.webp,.bmp,.pdf,image/jpeg,image/png,image/gif,image/webp,image/bmp,application/pdf"
                  onChange={handleFileUpload("workSamplesImages", true)}
                />
              </Button>
            </CardActions>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Paper
            sx={{ p: 2, bgcolor: "info.light", color: "info.contrastText" }}
          >
            <Typography variant="body2">
              <strong>راهنمای بارگذاری:</strong>
            </Typography>
            <Typography variant="body2" component="ul" sx={{ mt: 1, mb: 0 }}>
              <li>فرمت‌های مجاز: JPG, PNG, PDF</li>
              <li>حداکثر حجم هر فایل: ۵ مگابایت</li>
              <li>کیفیت تصاویر باید واضح و خوانا باشد</li>
              <li>مدارک باید معتبر و به‌روز باشند</li>
            </Typography>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="body2" color="text.secondary">
            * فیلدهای اجباری
          </Typography>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DocumentsStep;
